import { useContext, createContext, useMemo, forwardRef } from 'react'

import 'twin.macro'
import type {
  VerticalMenuAnchorProps,
  VerticalMenuButtonProps,
  VerticalMenuProps,
  VerticalMenuSectionLabelProps,
  VerticalMenuSectionProps,
} from './types'
import {
  StyledAnchor,
  StyledNav,
  Section,
  SectionLabel,
} from './VerticalMenu.styled'

const VerticalMenuContext = createContext<Pick<VerticalMenuProps, 'isActive'>>(
  {},
)

const VerticalMenu = ({ children, isActive, ...props }: VerticalMenuProps) => {
  const context = useMemo(() => {
    return {
      isActive,
    }
  }, [isActive])

  return (
    <VerticalMenuContext.Provider value={context}>
      <StyledNav {...props}>{children}</StyledNav>
    </VerticalMenuContext.Provider>
  )
}

VerticalMenu.SectionLabel = function VerticalMenuSectionLabel({
  children,
  ...props
}: VerticalMenuSectionLabelProps) {
  return <SectionLabel {...props}>{children}</SectionLabel>
}

VerticalMenu.Section = function VerticalMenuSection({
  children,
  ...props
}: VerticalMenuSectionProps) {
  return <Section {...props}>{children}</Section>
}

VerticalMenu.Anchor = forwardRef<HTMLAnchorElement, VerticalMenuAnchorProps>(
  function VerticalMenuAnchor({ children, href, ...props }, ref) {
    const context = useContext(VerticalMenuContext)

    if (context == null) {
      console.error(
        'Missing context for <VerticalMenu.Anchor />, make sure to wrap it with <VerticalMenu />',
      )

      return null
    }

    const { isActive } = context

    const isActivePage = Boolean(isActive?.(href))

    return (
      <StyledAnchor
        {...props}
        ref={ref}
        href={href}
        aria-current={isActivePage ? 'page' : undefined}
        isActive={isActivePage}
        data-current={isActivePage}
      >
        {children}
      </StyledAnchor>
    )
  },
)

VerticalMenu.Button = function VerticalMenuButton({
  children,
  onClick,
  ...props
}: VerticalMenuButtonProps) {
  return (
    <StyledAnchor
      {...props}
      onClick={onClick}
      as="button"
      type="button"
      tw="block text-left text-default-muted w-full"
    >
      {children}
    </StyledAnchor>
  )
}

export default VerticalMenu
